import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-bce59872"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "a-plan-manual box-flex-column" }
const _hoisted_2 = { class: "page-header" }
const _hoisted_3 = { class: "page-header__left" }
const _hoisted_4 = { class: "page-title" }
const _hoisted_5 = { class: "page-header__info" }
const _hoisted_6 = {
  key: 0,
  class: "info-item"
}
const _hoisted_7 = {
  key: 1,
  class: "page-tip"
}
const _hoisted_8 = { class: "page-header__actions" }
const _hoisted_9 = { class: "table-wrap" }
const _hoisted_10 = { class: "scene-bar" }
const _hoisted_11 = { class: "scene-count" }

import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  BaseButton,
  useCreateModal,
  useSelectColumn,
  XrkMessage,
  XrkTable,
  XrkTableProps,
  XrkTableRef
} from 'xrk-components';
import { storeToRefs } from 'pinia';
import appStore from '@/store';
import { deptListPageCmb } from '@/views/form-manage/src/data-source/apis';
import { sceneManageDetail, sceneManageSave } from '../data-source/apis';
import { sceneManageToDisplayTranslator } from '../data-source/translators';
import {
  expandDetailCol,
  indexCol,
  linkedProductCol,
  sceneNameCol,
  medicalRepTagCol,
  terminalServiceCol,
  projectIntroCol,
  platformPriceCol,
  updateDtCol
} from '../fields/columns';
import type {
  SceneFlatItem,
  SceneManageProductItem
} from '../data-source/types';
import SceneSelectDialog from '../components/scene-select-dialog.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'a-plan-manual',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const { info: userInfo } = storeToRefs(appStore.useUserStoreSetup);

const loading = ref(false);
const industryId = ref<number | undefined>();
const industryName = ref('');
const planId = ref<number | undefined>();
const planData = ref<SceneFlatItem[]>([]);
/** 原始勾选数据（SceneManageProductItem[]，保存时整包提交） */
const productsData = ref<SceneManageProductItem[]>([]);
/** 编辑模式乐观锁：detail 返回的主表 updateDt，保存时作为 lastUpdateDt 传回 */
const lastUpdateDt = ref<string>('');
const xrkTableRef = ref<XrkTableRef>(null);

const aspId = computed(() => Number((userInfo.value as any)?.finalDepId) || 0);

// ======================== XrkTable 配置 ========================

const paginationConfig: XrkTableProps['paginationConfig'] = {
  paginationPageSize: 15
};

const columns = [
  expandDetailCol,
  indexCol,
  linkedProductCol,
  sceneNameCol,
  medicalRepTagCol,
  terminalServiceCol,
  projectIntroCol,
  platformPriceCol,
  updateDtCol
];

const tableConfig: XrkTableProps['tableConfig'] = {
  baseConfig: { rowKey: '_id' },
  tableColumns: columns
};

/** 前端本地分页：全量数据在 planData 中，翻页做本地切片 */
const getListAjax = async (params: Record<string, any> = {}) => {
  const { pageNum = 1, pageSize = 15 } = params || {};
  const list = planData.value;
  const start = (pageNum - 1) * pageSize;
  return {
    data: {
      list: list.slice(start, start + pageSize),
      total: list.length
    }
  };
};

// ======================== XrkTable 配置 ========================

/** 新建模式：搜索区承载工业选择；编辑模式：隐藏搜索区（页头只读展示） */
const searchConfig = computed<XrkTableProps['searchConfig']>(() => ({
  hide: !!planId.value,
  searchBtn: { show: false },
  resetBtn: { show: false },
  searchColumns: [
    useSelectColumn({
      prop: 'industryId',
      label: '工业名称',
      config: {
        placeholder: '请选择工业名称',
        clearable: true,
        filterUseRemote: true,
        labelKey: 'entpName',
        valueKey: 'deptId',
        remoteMethod: (name: string) =>
          deptListPageCmb({
            pageNum: 1,
            pageSize: 50,
            enterpriseName: name || '',
            aspId: userInfo.value.finalDepId
          }).then((res: any) => {
            const list = res?.data?.list || [];
            // 只保留工业（functionCode=1），过滤服务商/供应商
            res.data.list = list.filter((e: any) => +e.functionCode === 1);
            return res;
          })
      },
      listener: {
        onChange: (_val: any, _searchData: any) => {
          industryName.value = _searchData?.[0]?.entpName || '';
          industryId.value = _val != null ? Number(_val) : undefined;
          // 工业变化（含首次选择/清空）→ 清场景列表（新建不调 detail，数据由弹窗打开时经 product/page + scene/page 获取）
          planData.value = [];
          productsData.value = [];
          xrkTableRef.value?.initList?.();
        }
      }
    })
  ] as any[]
}));

/** translator 输出 → 表格展示行（映射 terminalChannelText/serviceObjectText 到表格列字段） */
const toSceneFlatItems = (
  products: SceneManageProductItem[]
): SceneFlatItem[] =>
  sceneManageToDisplayTranslator(products).map(scene => ({
    ...scene,
    terminalChannel: scene.terminalChannelText || '',
    serviceObject: scene.serviceObjectText || ''
  })) as unknown as SceneFlatItem[];

// ======================== 详情加载 ========================

/** 加载详情（编辑模式：有 planId 时调用，sceneManageDetail 返回 products[].scenes[] 双层结构） */
const loadDetail = async () => {
  if (!industryId.value || !planId.value) return;
  loading.value = true;
  try {
    const res = await sceneManageDetail({
      planId: planId.value,
      industryId: industryId.value,
      aspId: aspId.value || undefined,
      detailMode: 2
    });
    const detail = res.data;
    // V2：detail 只返回已配置勾选数据（无 selected）
    productsData.value = detail?.products || [];
    // 编辑模式乐观锁：记录主表 updateDt，保存时作为 lastUpdateDt 传回
    lastUpdateDt.value = detail?.updateDt || '';
    // 展示数据：translator 按 sceneId 合并（场景挂产品子集）
    planData.value = toSceneFlatItems(productsData.value);
    // 详情模式：展示接口返回的工业名称（只读）
    if (detail?.industryName) {
      industryName.value = detail.industryName;
    }
    xrkTableRef.value?.initList?.();
  } catch (error) {
    console.error('获取详情失败:', error);
    XrkMessage.error('获取详情失败');
  } finally {
    loading.value = false;
  }
};

// ======================== 选择场景 ========================

/** 打开场景选择弹窗 */
const onOpenPicker = () => {
  if (!industryId.value) {
    XrkMessage.warning('请先选择工业名称');
    return;
  }

  useCreateModal(SceneSelectDialog, {
    products: productsData.value,
    industryName: industryName.value,
    industryId: industryId.value,
    aspId: aspId.value || undefined,
    planId: planId.value,
    onConfirm: (products: SceneManageProductItem[]) => {
      onSceneConfirm(products);
    }
  });
};

/** 场景选择弹窗确认回调：用 emit 全量数据替换本地 products + translator 重塑展示（不再调接口，保存统一走右上角按钮） */
const onSceneConfirm = (products: SceneManageProductItem[]) => {
  productsData.value = products;
  planData.value = toSceneFlatItems(products);
  xrkTableRef.value?.initList?.();
};

// ======================== 保存 ========================

/** 页面右上角保存：校验工业+场景数据后调 config/save 全量更新 */
const onSave = async () => {
  if (!industryId.value) {
    XrkMessage.warning('请先选择工业名称');
    return;
  }
  if (productsData.value.length === 0) {
    XrkMessage.warning('请先选择场景');
    return;
  }
  loading.value = true;
  try {
    // V2 整包保存：只提交勾选数据（无 selected）；编辑时传 lastUpdateDt 乐观锁；totalBudget 需求不做不传
    const res = await sceneManageSave({
      planScope: 2,
      industryId: industryId.value,
      aspId: aspId.value,
      createType: 1,
      planId: planId.value || null,
      lastUpdateDt: lastUpdateDt.value || undefined,
      products: productsData.value
    });
    if (!planId.value && res.data?.planId) {
      planId.value = res.data.planId;
    }
    XrkMessage.success('保存成功');
    // 保存后回到列表
    router.push({
      path: '/main/a/scene-mgmt/main',
      query: { tab: route.query.tab }
    });
  } catch (error) {
    console.error('保存失败:', error);
    XrkMessage.error('保存失败');
  } finally {
    loading.value = false;
  }
};

// ======================== 取消 ========================

const onCancel = () => {
  router.push({
    path: '/main/a/scene-mgmt/main',
    query: { tab: route.query.tab }
  });
};

// ======================== 初始化 ========================

onMounted(() => {
  // 编辑模式：从 query 读取 planId/industryId，加载详情
  const queryPlanId = route.query.planId;
  const queryIndustryId = route.query.industryId;
  if (queryPlanId) {
    planId.value = Number(queryPlanId);
  }
  if (queryIndustryId) {
    industryId.value = Number(queryIndustryId);
  }
  if (planId.value && industryId.value) {
    loadDetail();
  }
});

return (_ctx: any,_cache: any) => {
  const _directive_xrk_loading = _resolveDirective("xrk-loading")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h1", _hoisted_4, _toDisplayString(planId.value ? '查看企业策划' : '新增企业策划'), 1),
        _createElementVNode("div", _hoisted_5, [
          (planId.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_6, [
                _createTextVNode(" 工业名称： "),
                _createElementVNode("b", null, _toDisplayString(industryName.value || '--'), 1)
              ]))
            : (_openBlock(), _createElementBlock("p", _hoisted_7, " 选择工业后，点击「选择场景」从场景库挑选推荐场景，确认后进入策划列表统一管理。 "))
        ])
      ]),
      _createElementVNode("div", _hoisted_8, [
        _createVNode(_unref(BaseButton), {
          type: "default",
          onClick: onCancel
        }, {
          default: _withCtx(() => [
            _createTextVNode("返回")
          ]),
          _: 1
        }),
        _createVNode(_unref(BaseButton), {
          type: "primary",
          click: onSave
        }, {
          default: _withCtx(() => [
            _createTextVNode("保存")
          ]),
          _: 1
        })
      ])
    ]),
    _createElementVNode("div", _hoisted_9, [
      _createVNode(_unref(XrkTable), {
        ref_key: "xrkTableRef",
        ref: xrkTableRef,
        getListAjax: getListAjax,
        searchConfig: searchConfig.value,
        tableConfig: tableConfig,
        paginationConfig: paginationConfig
      }, {
        tableBefore: _withCtx(() => [
          _createElementVNode("div", _hoisted_10, [
            _createVNode(_unref(BaseButton), {
              type: "primary",
              onClick: onOpenPicker
            }, {
              default: _withCtx(() => [
                _createTextVNode(" 选择场景 ")
              ]),
              _: 1
            }),
            _createElementVNode("span", _hoisted_11, [
              _createTextVNode(" 已选择 "),
              _createElementVNode("b", null, _toDisplayString(planData.value.length), 1),
              _createTextVNode(" 个场景 ")
            ])
          ])
        ]),
        _: 1
      }, 8, ["searchConfig"])
    ])
  ])), [
    [_directive_xrk_loading, loading.value]
  ])
}
}

})